import { ethers } from 'ethers';

const ESCROW_ABI = [
  'function depositEscrow(bytes32 noticeId) payable',
  'function refundEscrow(bytes32 noticeId, address payable merchant)',
  'function resolveDispute(bytes32 noticeId, address payable recipient)',
  'event EscrowDeposited(bytes32 indexed noticeId, address indexed merchant, uint256 amount)',
  'event EscrowRefunded(bytes32 indexed noticeId, address indexed merchant, uint256 amount)',
  'event EscrowDisputeResolved(bytes32 indexed noticeId, address indexed recipient, uint256 amount)',
];

declare global {
  interface Window {
    __ESCROW_CONTRACT_ADDRESS__?: string;
    __SPOKE_CONTRACT_ADDRESS__?: string;
    __SPOKE_CHAIN_ID__?: number;
    __E2E_CHAIN_ID__?: number;
    ethereum?: {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on?: (event: string, handler: (...args: unknown[]) => void) => void;
      removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
    };
  }
}

const SPOKE_ABI = [
  'function depositAndRoute(bytes32 noticeId, uint32 destinationDomain) payable',
  'event SpokeDepositRouted(bytes32 indexed noticeId, uint32 indexed destinationDomain, address indexed depositor, uint256 amount, bytes32 messageId)',
];

const DEFAULT_SPOKE_CHAIN_ID = 31338;
const DEFAULT_HUB_DOMAIN = 31337;

const resolveSpokeContractAddress = (): string => {
  const address =
    (typeof window !== 'undefined' && window.__SPOKE_CONTRACT_ADDRESS__) ||
    process.env.REACT_APP_SPOKE_CONTRACT_ADDRESS ||
    '';

  if (!address) {
    throw new Error('REACT_APP_SPOKE_CONTRACT_ADDRESS is not configured');
  }

  if (!ethers.utils.isAddress(address)) {
    throw new Error('REACT_APP_SPOKE_CONTRACT_ADDRESS is invalid');
  }

  return ethers.utils.getAddress(address);
};

const resolveSpokeChainId = (): number => {
  if (typeof window !== 'undefined' && window.__SPOKE_CHAIN_ID__) {
    return window.__SPOKE_CHAIN_ID__;
  }

  const configured = process.env.REACT_APP_SPOKE_CHAIN_ID;
  return configured ? Number(configured) : DEFAULT_SPOKE_CHAIN_ID;
};

const resolveHubDomain = (): number => {
  const configured = process.env.REACT_APP_HUB_DOMAIN || process.env.REACT_APP_HUB_CHAIN_ID;
  return configured ? Number(configured) : DEFAULT_HUB_DOMAIN;
};

const isOmnichainFundingEnabled = (): boolean =>
  Boolean(
    (typeof window !== 'undefined' && window.__SPOKE_CONTRACT_ADDRESS__) ||
      process.env.REACT_APP_SPOKE_CONTRACT_ADDRESS
  );

const resolveEscrowContractAddress = (): string => {
  const address =
    (typeof window !== 'undefined' && window.__ESCROW_CONTRACT_ADDRESS__) ||
    process.env.REACT_APP_ESCROW_CONTRACT_ADDRESS ||
    process.env.REACT_APP_ESCROW_CONTRACT ||
    '';

  if (!address) {
    throw new Error('REACT_APP_ESCROW_CONTRACT_ADDRESS is not configured');
  }

  if (!ethers.utils.isAddress(address)) {
    throw new Error('REACT_APP_ESCROW_CONTRACT_ADDRESS is invalid');
  }

  return ethers.utils.getAddress(address);
};

export const encodeNoticeIdBytes32 = (noticeId: string): string => {
  if (noticeId.length <= 31) {
    return ethers.utils.formatBytes32String(noticeId);
  }
  return ethers.utils.keccak256(ethers.utils.toUtf8Bytes(noticeId));
};

export const connectWeb3Wallet = async (): Promise<ethers.providers.Web3Provider> => {
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No Web3 wallet detected. Install MetaMask or Rabby.');
  }

  await window.ethereum.request({ method: 'eth_requestAccounts' });
  return new ethers.providers.Web3Provider(window.ethereum);
};

export const getEscrowContract = async (
  provider?: ethers.providers.Web3Provider
): Promise<ethers.Contract> => {
  const web3Provider = provider || (await connectWeb3Wallet());
  const signer = web3Provider.getSigner();
  return new ethers.Contract(resolveEscrowContractAddress(), ESCROW_ABI, signer);
};

export const switchToSpokeNetwork = async (): Promise<void> => {
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No Web3 wallet detected. Install MetaMask or Rabby.');
  }

  const chainId = resolveSpokeChainId();
  const chainIdHex = ethers.utils.hexValue(chainId);

  await window.ethereum.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: chainIdHex }],
  });
};

export const getSpokeContract = async (
  provider?: ethers.providers.Web3Provider
): Promise<ethers.Contract> => {
  const web3Provider = provider || (await connectWeb3Wallet());
  const signer = web3Provider.getSigner();
  return new ethers.Contract(resolveSpokeContractAddress(), SPOKE_ABI, signer);
};

export const fundEscrowViaSpoke = async (
  noticeId: string,
  amountEth: string
): Promise<ethers.providers.TransactionReceipt> => {
  await switchToSpokeNetwork();
  const provider = await connectWeb3Wallet();
  const contract = await getSpokeContract(provider);
  const noticeIdBytes32 = encodeNoticeIdBytes32(noticeId);
  const value = ethers.utils.parseEther(amountEth);
  const destinationDomain = resolveHubDomain();

  const tx = await contract.depositAndRoute(noticeIdBytes32, destinationDomain, { value });
  const receipt = await tx.wait();

  if (!receipt) {
    throw new Error('Spoke deposit transaction did not return a receipt');
  }

  if (receipt.status !== 1) {
    throw new Error('Spoke deposit transaction failed on chain');
  }

  return receipt;
};

export const fundEscrow = async (
  noticeId: string,
  amountEth: string
): Promise<ethers.providers.TransactionReceipt> => {
  if (isOmnichainFundingEnabled()) {
    return fundEscrowViaSpoke(noticeId, amountEth);
  }

  const provider = await connectWeb3Wallet();
  const contract = await getEscrowContract(provider);
  const noticeIdBytes32 = encodeNoticeIdBytes32(noticeId);
  const value = ethers.utils.parseEther(amountEth);

  const tx = await contract.depositEscrow(noticeIdBytes32, { value });
  const receipt = await tx.wait();

  if (!receipt) {
    throw new Error('Escrow deposit transaction did not return a receipt');
  }

  if (receipt.status !== 1) {
    throw new Error('Escrow deposit transaction failed on chain');
  }

  return receipt;
};
